'use client';
/**
 * L'ENTRETIEN DE DÉCLARATION AVEC L'ASSISTANT (Claude, 19/10/2026).
 *
 * Le technicien raconte ce qui s'est passé ; l'assistant pose, s'il le faut,
 * quelques questions, puis REMPLIT le formulaire de déclaration qui est juste
 * en dessous. Le technicien relit les trois étapes, corrige, et c'est son clic
 * « Créer » qui enregistre la NC et la soumet au RQ.
 *
 * Présenté comme une conversation (20/10/2026) : on parle à l'assistant comme
 * à un collègue, en français ou en tounsi — en lettres latines, au clavier
 * français (« lyoum sbe7 jani tube m3a9ed »). La fiche, elle, revient toujours
 * en français (consigne côté serveur). Aux couleurs de l'assistant (`--ia-*`).
 *
 * Remplace « Suggérer un classement » (moteur local, plusieurs minutes).
 *
 * ── Ce que ce composant ne fait jamais ──────────────────────────────────────
 *
 * Il n'écrit rien : il rend une fiche à l'appelant (`onFiche`), avec la
 * conversation qui y a mené. Celle-ci part avec la déclaration et se garde
 * avec la NC, lisible par la RQ et le déclarant (RQ, 03/11/2026) — ce que
 * l'en-tête dit au technicien. Une conversation abandonnée ne se garde pas.
 *
 * Masqué quand le serveur n'a pas de clé Anthropic (`iaEntretienNC`) : le
 * formulaire se remplit très bien à la main.
 */
import React, { useState } from 'react';
import { useGed } from '@/lib/store';
import { I, AvatarIA, boutonIA, boutonIAPlein, inputStyle } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';

export interface FicheEntretien {
  titre: string;
  description: string;
  nature: string;
  canal: string;
  gravite: string;
  impactResultats: string;
  correction: string;
  dateDetection: string;
  dossiers: string[];
  manques: string;
}

export interface Echange { role: 'declarant' | 'assistant'; texte: string }

/** Une bulle : l'assistant à gauche, à sa couleur ; le technicien à droite. */
export function Bulle({ de, children }: { de: 'declarant' | 'assistant'; children: React.ReactNode }) {
  const assistant = de === 'assistant';
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', justifyContent: assistant ? 'flex-start' : 'flex-end' }}>
      {assistant && <AvatarIA />}
      <div style={{
        maxWidth: '82%', whiteSpace: 'pre-wrap', padding: '9px 13px', fontSize: 13.5, lineHeight: 1.5,
        color: 'var(--text-strong)',
        background: assistant ? 'var(--ia-surface)' : 'var(--surface-card)',
        border: assistant ? '1px solid var(--ia-bord)' : '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        borderBottomLeftRadius: assistant ? 'var(--radius-xs)' : 'var(--radius-lg)',
        borderBottomRightRadius: assistant ? 'var(--radius-lg)' : 'var(--radius-xs)',
      }}>
        {children}
      </div>
    </div>
  );
}

export function EntretienDeclarationNC({ onFiche }: { onFiche: (f: FicheEntretien, echanges: Echange[]) => void }) {
  const { iaEntretienNC, entretienNC } = useGed();
  const [echanges, setEchanges] = useState<Echange[]>([]);
  const [questions, setQuestions] = useState<string[]>([]);
  const [saisie, setSaisie] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');
  const [manques, setManques] = useState<string | null>(null);
  if (!iaEntretienNC) return null;

  const termine = manques !== null;
  const recit = echanges[0]?.texte;

  const envoyer = async (conclure: boolean) => {
    if (occupe) return;
    const texte = saisie.trim();
    // Conclure sans rien taper : l'échange doit tout de même finir par le
    // déclarant (le serveur le vérifie), et c'est bien ce qu'il a dit.
    const suite: Echange[] = [...echanges, { role: 'declarant', texte: texte || 'Je n’ai rien à ajouter.' }];
    if (!echanges.length && !texte) return;
    if (!conclure && !texte) return;
    // Le message s'affiche tout de suite ; l'assistant « écrit » en dessous.
    setEchanges(suite); setSaisie('');
    setOccupe(true); setErreur('');
    const res = await entretienNC(suite, conclure);
    setOccupe(false);
    if (!res.ok) { setEchanges(echanges); setSaisie(texte); setErreur(res.message); return; }
    const data = res.reponse;
    if (data.etat === 'questions' && Array.isArray(data.questions) && data.questions.length) {
      const qs = data.questions.map(String);
      setEchanges([...suite, { role: 'assistant', texte: qs.join('\n') }]);
      setQuestions(qs);
      return;
    }
    if (data.etat === 'fiche' && data.fiche && typeof data.fiche === 'object') {
      const fiche = data.fiche as FicheEntretien;
      setQuestions([]);
      setManques(String(fiche.manques || ''));
      onFiche(fiche, suite);
      return;
    }
    setEchanges(echanges); setSaisie(texte);
    setErreur('Réponse de l’assistant illisible. Remplissez la fiche à la main.');
  };

  const recommencer = () => { setEchanges([]); setQuestions([]); setSaisie(''); setErreur(''); setManques(null); };

  // Entrée envoie, Maj+Entrée va à la ligne — comme dans une messagerie.
  const auClavier = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (saisie.trim()) void envoyer(false);
    }
  };

  return (
    <section aria-label="Conversation avec l’assistant de déclaration" style={{
      marginBottom: 18, background: 'var(--surface-sunken)', border: '1px solid var(--ia-bord)',
      borderRadius: 'var(--radius-lg)', overflow: 'hidden',
    }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--ia-surface)', borderBottom: '1px solid var(--ia-bord)' }}>
        <AvatarIA />
        <div style={{ display: 'grid', gap: 1 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ia-encre)' }}>Votre assistant de déclaration</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Il vous écoute, pose quelques questions et remplit la fiche pour vous.
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            La conversation est jointe à la non-conformité et lisible par le responsable qualité.
          </span>
        </div>
      </header>

      <div style={{ display: 'grid', gap: 10, padding: 14 }}>
        <Bulle de="assistant">
          3aslema ! Racontez-moi ce qui s’est passé, avec vos mots, comme vous le diriez à un collègue.
          Pas besoin de phrases parfaites.
          {'\n\n'}
          <strong>Écrivez comme vous parlez :</strong> en français, en tounsi ou en mélangeant les deux
          — « lyoum sbe7 jani tube m3a9ed… », je comprends. La fiche sera rédigée en français.
        </Bulle>

        {echanges.map((e, i) => (
          <Bulle key={i} de={e.role}>
            {e.role === 'assistant' && e.texte.includes('\n')
              ? <ol style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 4 }}>
                  {e.texte.split('\n').map((q, j) => <li key={j}>{q}</li>)}
                </ol>
              : e.texte}
          </Bulle>
        ))}

        {occupe && (
          <Bulle de="assistant">
            <span role="status" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>L’assistant lit votre message…</span>
          </Bulle>
        )}

        {termine && (
          <Bulle de="assistant">
            <span role="status">
              <strong>Merci ! La fiche ci-dessous est remplie.</strong> Relisez-la tranquillement, corrigez ce qui ne va pas,
              puis créez la non-conformité : c’est vous qui l’envoyez.
              {manques ? `\n\nIl manque encore : ${manques}` : ''}
            </span>
          </Bulle>
        )}

        {termine ? (
          <div><Bouton taille="sm" variante="discret" onClick={recommencer}>Recommencer la conversation</Bouton></div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea value={saisie} onChange={(e) => setSaisie(e.target.value)} onKeyDown={auClavier}
                rows={recit ? 2 : 3}
                aria-label={recit ? 'Votre réponse aux questions de l’assistant' : 'Raconter ce qui s’est passé à l’assistant'}
                placeholder={recit
                  ? 'Répondez à ce que vous savez — « je ne sais pas » (« ma na3rafch ») est une bonne réponse.'
                  : 'Ex. : ce matin, tube EDTA reçu coagulé pour le dossier 261019/0042, NFS non faite, j’ai demandé un nouveau prélèvement.'}
                disabled={occupe}
                style={{ ...inputStyle, flex: 1, height: 'auto', padding: '10px 12px', resize: 'vertical', background: 'var(--surface-card)' }} />
              <Bouton taille="md" variante="primaire" style={boutonIAPlein} onClick={() => void envoyer(false)}
                disabled={!saisie.trim() || occupe}>
                <I n="send" s={15} />{recit ? 'Répondre' : 'Envoyer'}
              </Bouton>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--text-muted)' }}>
                <I n="lock" s={12} />Pas de nom de patient : le n° de dossier suffit, il est masqué avant l’envoi.
              </span>
              {questions.length > 0 && (
                <Bouton taille="sm" variante="discret" style={boutonIA} onClick={() => void envoyer(true)} disabled={occupe}>
                  Remplir la fiche maintenant
                </Bouton>
              )}
            </div>
          </div>
        )}

        {erreur && <p role="alert" style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-700)' }}>{erreur}</p>}
      </div>
    </section>
  );
}
